import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { State } from 'ts-fsrs';
import {
  AudioButton,
  Button,
  Container,
  Heading,
  Muted,
  Screen,
  Skeleton,
  Stack,
  Text,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { useAudio } from '../audio/audioPlayer.js';
import { useDeckOverview } from './hooks.js';
import type { CardStateRow, DeckEntry } from './deck.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import './DeckBrowser.css';

/**
 * Deck browser — the old vocab glossary, upgraded with each card's SRS
 * status (new / learning / known / due now). Curriculum-ordered.
 */
export function DeckBrowser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [now] = useState(() => new Date());
  const { entries, states, loaded } = useDeckOverview(now);

  if (!user) return null;

  return (
    <Screen>
      <Container size="sm">
        <Stack
          gap={6}
          style={{
            padding:
              'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + 88px + env(safe-area-inset-bottom))',
          }}
        >
          <Stack gap={2}>
            <div className="fluent-fc-browse__top">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/flashcards')}
                aria-label={t('flashcards.browse_back')}
              >
                ←
              </Button>
              <Heading level={1}>{t('flashcards.browse_title')}</Heading>
            </div>
            <Muted>{t('flashcards.browse_subtitle', { count: entries.length })}</Muted>
          </Stack>

          {!loaded ? (
            <Stack gap={2}>
              <Skeleton shape="card" style={{ height: 64 }} />
              <Skeleton shape="card" style={{ height: 64 }} />
              <Skeleton shape="card" style={{ height: 64 }} />
            </Stack>
          ) : entries.length === 0 ? (
            <Stack gap={2} align="center" style={{ padding: 'var(--space-8, 32px) var(--space-4, 16px)', textAlign: 'center' }}>
              <span style={{ fontSize: 'var(--text-5xl, 48px)' }} aria-hidden="true">🗂️</span>
              <Heading level={3}>{t('flashcards.empty_title')}</Heading>
              <Text tone="muted">{t('flashcards.empty_body')}</Text>
            </Stack>
          ) : (
            <Stack gap={2}>
              {entries.map((entry) => (
                <CardRow key={entry.key} entry={entry} state={states.get(entry.key)} now={now} />
              ))}
            </Stack>
          )}
        </Stack>
      </Container>
      <AppBottomNav />
    </Screen>
  );
}

function CardRow({
  entry,
  state,
  now,
}: {
  entry: DeckEntry;
  state: CardStateRow | undefined;
  now: Date;
}) {
  const { t } = useTranslation();
  const { playing, error, play, retry } = useAudio(entry.audio_key ?? null);
  const status = statusOf(state, now);

  return (
    <div className="fluent-fc-browse__row">
      <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
        <Text weight="bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {entry.en}
        </Text>
        <Text tone="muted" size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {entry.ka}
        </Text>
      </Stack>
      <span className="fluent-fc-browse__chip" data-status={status}>
        {t(`flashcards.status.${status}`)}
      </span>
      {entry.audio_key && (
        <AudioButton size="md" playing={playing} onClick={error ? retry : play} />
      )}
    </div>
  );
}

type CardStatus = 'new' | 'learning' | 'due' | 'known';

function statusOf(state: CardStateRow | undefined, now: Date): CardStatus {
  if (!state) return 'new';
  if (state.due.getTime() <= now.getTime()) return 'due';
  if (state.state === State.Learning || state.state === State.Relearning) return 'learning';
  if (state.state === State.Review) return 'known';
  return 'new';
}
